import { useState } from 'react';
import { Plus, Edit, Trash2, Save, X, MapPin } from 'lucide-react';
import { nearbyPlaces as initialNearbyPlaces } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import type { NearbyPlace } from '../../data/mockData';

export default function AdminNearby() {
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlace[]>(initialNearbyPlaces);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPlace, setEditingPlace] = useState<NearbyPlace | null>(null);
  const [newPlace, setNewPlace] = useState<Partial<NearbyPlace>>({
    name: '',
    nameEn: '',
    icon: 'GraduationCap'
  });
  const [successMessage, setSuccessMessage] = useState('');

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذا المكان؟')) {
      setNearbyPlaces(nearbyPlaces.filter(p => p.id !== id));
      setSuccessMessage('تم حذف المكان بنجاح!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleEdit = (place: NearbyPlace) => {
    setEditingPlace({ ...place });
    setIsEditDialogOpen(true);
  };

  const handleAdd = () => {
    if (newPlace.name && newPlace.nameEn && newPlace.icon) {
      const place: NearbyPlace = {
        id: Date.now().toString(),
        name: newPlace.name,
        nameEn: newPlace.nameEn,
        icon: newPlace.icon
      };
      setNearbyPlaces([...nearbyPlaces, place]);
      setNewPlace({ name: '', nameEn: '', icon: 'GraduationCap' });
      setIsAddDialogOpen(false);
      setSuccessMessage('تم إضافة المكان بنجاح!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleUpdate = () => {
    if (editingPlace) {
      setNearbyPlaces(nearbyPlaces.map(p => 
        p.id === editingPlace.id ? editingPlace : p
      ));
      setIsEditDialogOpen(false);
      setEditingPlace(null);
      setSuccessMessage('تم تحديث المكان بنجاح!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const getIconEmoji = (iconName: string) => {
    const iconMap: { [key: string]: string } = {
      'GraduationCap': '🎓',
      'Hospital': '🏥',
      'Bus': '🚌',
      'ShoppingBag': '🛍️',
      'UtensilsCrossed': '🍽️',
      'Building': '🏛️'
    };
    return iconMap[iconName] || '📍';
  };

  return (
    <div>
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
          <span>{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">إدارة الأماكن القريبة</h1>
          <p className="text-gray-600">إضافة وتعديل وحذف الأماكن القريبة من العقارات</p>
        <div className="bg-[var(--color-gold-50)] border border-[var(--color-gold-200)] rounded-lg px-4 py-2">
          <span className="text-[var(--color-gold-800)] font-medium">
            إجمالي الأماكن: {nearbyPlaces.length}
          </span>
        </div>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[var(--color-gold-600)] hover:bg-[var(--color-gold-700)] text-white">
              <Plus className="w-5 h-5 ml-2" />
              إضافة مكان جديد
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>إضافة مكان قريب جديد</DialogTitle>
            </DialogHeader>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
              <div>
                <label className="block text-sm font-medium mb-2">اسم المكان (عربي)</label>
                <Input 
                  placeholder="مثال: مدارس" 
                  value={newPlace.name}
                  onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">اسم المكان (إنجليزي)</label>
                <Input 
                  placeholder="Example: schools" 
                  value={newPlace.nameEn}
                  onChange={(e) => setNewPlace({ ...newPlace, nameEn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الأيقونة</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={newPlace.icon}
                  onChange={(e) => setNewPlace({ ...newPlace, icon: e.target.value })}
                >
                  <option value="GraduationCap">🎓 مدارس</option>
                  <option value="Hospital">🏥 مستشفيات</option>
                  <option value="Bus">🚌 مواصلات</option>
                  <option value="ShoppingBag">🛍️ مولات</option>
                  <option value="UtensilsCrossed">🍽️ مطاعم</option>
                  <option value="Building">🏛️ مساجد</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-[var(--color-gold-600)] hover:bg-[var(--color-gold-700)] text-white">
                  <Save className="w-4 h-4 ml-2" />
                  حفظ المكان
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setIsAddDialogOpen(false);
                    setNewPlace({ name: '', nameEn: '', icon: 'GraduationCap' });
                  }}
                >
                  <X className="w-4 h-4 ml-2" />
                  إلغاء
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Nearby Places Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {nearbyPlaces.length > 0 ? (
          nearbyPlaces.map((place) => (
          <div key={place.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-14 h-14 bg-[var(--color-gold-50)] rounded-full flex items-center justify-center mb-3">
                <span className="text-[var(--color-gold-600)] text-2xl">{getIconEmoji(place.icon)}</span>
              </div>
              <h3 className="font-bold text-gray-800">{place.name}</h3>
              <p className="text-sm text-gray-600">{place.nameEn}</p>
              <p className="text-xs text-gray-400 mt-1">ID: {place.id}</p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => handleEdit(place)}
              >
                <Edit className="w-4 h-4 ml-2" />
                تعديل
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(place.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))
        ) : (
          <div className="col-span-full text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">لا توجد أماكن قريبة</h3>
            <p className="text-gray-500 mb-4">ابدأ بإضافة أول مكان قريب</p>
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-[var(--color-gold-600)] hover:bg-[var(--color-gold-700)] text-white"
            >
              <Plus className="w-4 h-4 ml-2" />
              إضافة مكان جديد
            </Button>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تعديل المكان القريب</DialogTitle>
          </DialogHeader>
          {editingPlace && (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
              <div>
                <label className="block text-sm font-medium mb-2">اسم المكان (عربي)</label>
                <Input 
                  value={editingPlace.name}
                  onChange={(e) => setEditingPlace({ ...editingPlace, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">اسم المكان (إنجليزي)</label>
                <Input 
                  value={editingPlace.nameEn}
                  onChange={(e) => setEditingPlace({ ...editingPlace, nameEn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الأيقونة</label>
                <select 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={editingPlace.icon}
                  onChange={(e) => setEditingPlace({ ...editingPlace, icon: e.target.value })}
                >
                  <option value="GraduationCap">🎓 مدارس</option>
                  <option value="Hospital">🏥 مستشفيات</option>
                  <option value="Bus">🚌 مواصلات</option>
                  <option value="ShoppingBag">🛍️ مولات</option>
                  <option value="UtensilsCrossed">🍽️ مطاعم</option>
                  <option value="Building">🏛️ مساجد</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-[var(--color-gold-600)] hover:bg-[var(--color-gold-700)] text-white">
                  <Save className="w-4 h-4 ml-2" />
                  حفظ التعديلات
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingPlace(null);
                  }}
                >
                  <X className="w-4 h-4 ml-2" />
                  إلغاء
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}